/**
 * Bee Hive Partner Website V2 — unified product storefront (Phase I).
 * ONE dataset, ONE grid: Favorites (first 4) → expand in place → full Shop.
 */
(function (global) {
  'use strict';

  var CONTRACT = 'bee_hive_partner_product_commerce_v1';
  var FAVORITES_MAX = 4;
  var SHOP_MAX = 24;

  function trim(v) {
    return String(v == null ? '' : v).trim();
  }

  function commerceKey(item) {
    return trim(item.shopify_product_id || item.id) + ':' + trim(item.selected_variant_id || item.variant_id);
  }

  function parseCents(cents) {
    if (cents == null || cents === '') return null;
    if (typeof cents === 'boolean') return null;
    if (typeof cents === 'string') {
      var raw = cents.trim();
      if (!raw || !/^-?\d+(\.\d+)?$/.test(raw)) return null;
      var fromStr = Number(raw);
      return isFinite(fromStr) ? Math.trunc(fromStr) : null;
    }
    var n = Number(cents);
    if (!isFinite(n)) return null;
    return Math.trunc(n);
  }

  function moneyLocale(locale) {
    var loc = String(locale || '').toLowerCase();
    if (loc.indexOf('en') === 0) return 'en-GB';
    return 'nl-NL';
  }

  function money(cents, locale) {
    var parsed = parseCents(cents);
    if (parsed == null) return '';
    var n = parsed / 100;
    try {
      return new Intl.NumberFormat(moneyLocale(locale), { style: 'currency', currency: 'EUR' }).format(n);
    } catch (e) {
      return '€' + n.toFixed(2);
    }
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function normalizeList(products) {
    var list = Array.isArray(products) ? products.slice() : [];
    list.sort(function (a, b) {
      var sa = Number(a.sort_order);
      var sb = Number(b.sort_order);
      if (!isFinite(sa)) sa = 0;
      if (!isFinite(sb)) sb = 0;
      return sa - sb;
    });
    return list.slice(0, SHOP_MAX).filter(function (p) {
      if (p.commerce_ready === false && (p.commerce_block_reason === 'missing_product' || p.product_exists === false)) {
        var handle = trim(p.product_handle || p.handle);
        var productId = trim(p.shopify_product_id || p.id);
        if (handle && (p.source === 'partner_selection' || productId)) {
          return true;
        }
        return false;
      }
      if (p.product_published === false && p.source === 'admin_graphql') return false;
      return true;
    });
  }

  /** @deprecated Phase I — use buildProductState */
  function splitFavoritesShop(products) {
    var all = normalizeList(products);
    return {
      all: all,
      favorites: all.slice(0, Math.min(FAVORITES_MAX, all.length)),
      shop: all,
    };
  }

  function buildProductState(products, expanded) {
    var allProducts = normalizeList(products);
    var isExpanded = Boolean(expanded);
    var needsExpand = allProducts.length > FAVORITES_MAX;
    var visibleProducts = isExpanded || !needsExpand ? allProducts : allProducts.slice(0, FAVORITES_MAX);
    return {
      allProducts: allProducts,
      visibleProducts: visibleProducts,
      expanded: isExpanded,
      needsExpand: needsExpand,
      favoritesCount: Math.min(FAVORITES_MAX, allProducts.length),
    };
  }

  function buildCommerceMap(products) {
    var map = Object.create(null);
    normalizeList(products).forEach(function (p) {
      map[commerceKey(p)] = p;
    });
    return map;
  }

  function isAttributableRoot(root) {
    return String(root.getAttribute('data-bh-sf-attributable') || '') === 'true';
  }

  function isSampleCartRoot(root) {
    return String(root.getAttribute('data-bh-sf-sample-cart') || '') === 'true';
  }

  function usesDemoCommerceUi(root) {
    return !isAttributableRoot(root) && !isSampleCartRoot(root);
  }

  function uiCopy(locale) {
    var en = String(locale || '').toLowerCase().indexOf('en') === 0;
    return en
      ? {
          more: 'More information',
          add: 'Add to cart',
          demo: 'Demo',
          demoHint: 'Demo page — cart disabled',
          favorite: 'Favorite',
          soldOut: 'Sold out',
          available: 'Available',
          priceUnavailable: 'Price unavailable',
          unavailable: 'Unavailable',
          expand: 'See all products',
          titleFav: 'Favorites',
          titleShop: 'Shop',
          eyebrow: 'Partner shop',
          productsMeta: 'products',
        }
      : {
          more: 'Meer informatie',
          add: 'In winkelwagen',
          demo: 'Voorbeeld',
          demoHint: 'Voorbeeldpagina — winkelwagen niet actief',
          favorite: 'Favoriet',
          soldOut: 'Uitverkocht',
          available: 'Beschikbaar',
          priceUnavailable: 'Prijs niet beschikbaar',
          unavailable: 'Niet beschikbaar',
          expand: 'Bekijk alle producten',
          titleFav: 'Favorieten',
          titleShop: 'Shop',
          eyebrow: 'Partnershop',
          productsMeta: 'producten',
        };
  }

  function readCopy(root) {
    var locale = trim(root.getAttribute('data-bh-sf-locale') || 'nl');
    var base = uiCopy(locale);
    return {
      more: trim(root.getAttribute('data-bh-sf-copy-more')) || base.more,
      add: trim(root.getAttribute('data-bh-sf-copy-add')) || base.add,
      demo: trim(root.getAttribute('data-bh-sf-copy-demo')) || base.demo,
      demoHint: trim(root.getAttribute('data-bh-sf-copy-demo-hint')) || base.demoHint,
      favorite: trim(root.getAttribute('data-bh-sf-copy-favorite')) || base.favorite,
      soldOut: base.soldOut,
      available: base.available,
      priceUnavailable: base.priceUnavailable,
      unavailable: base.unavailable,
      expand: trim(root.getAttribute('data-bh-sf-copy-expand')) || base.expand,
      titleFav: trim(root.getAttribute('data-bh-sf-copy-title-fav')) || base.titleFav,
      titleShop: trim(root.getAttribute('data-bh-sf-copy-title-shop')) || base.titleShop,
      eyebrow: trim(root.getAttribute('data-bh-sf-copy-eyebrow')) || base.eyebrow,
      productsMeta: base.productsMeta,
      empty: trim(root.getAttribute('data-bh-sf-copy-empty')) || (locale.indexOf('en') === 0 ? 'No products in this partner shop yet.' : 'Nog geen producten in deze partnershop.'),
    };
  }

  function cardModeForProduct(product, index, state) {
    if (index < state.favoritesCount) return 'favorite';
    return 'shop';
  }

  function cardHtml(product, mode, skin, locale, options) {
    options = options || {};
    var copy = options.copy || uiCopy(locale);
    var demoMode = Boolean(options.demoMode);
    var title = trim(product.title) || 'MUSKLE product';
    var variant = trim(product.selected_variant_label || product.variant_label);
    var img = trim(product.display_image_url || product.variant_image_url || product.image_url || product.product_image_url);
    var alt = trim(product.display_image_alt || product.variant_image_alt || title);
    var priceCents = parseCents(product.price_cents);
    var compareCents = parseCents(product.compare_at_price_cents);
    var price = money(priceCents, locale);
    var compare = money(compareCents, locale);
    var productId = trim(product.shopify_product_id || product.id);
    var variantId = trim(product.selected_variant_id || product.variant_id);
    var note = trim(product.partner_note || product.short_info || '');
    var soldOut = product.sold_out || product.available === false;
    var priceMissing = priceCents == null;
    var ready = product.commerce_ready !== false && !soldOut && !priceMissing;
    var key = commerceKey(product);
    var status = soldOut
      ? copy.soldOut
      : priceMissing
        ? copy.priceUnavailable
        : product.commerce_ready === false
          ? trim(product.commerce_block_reason || product.variant_attention || copy.unavailable)
          : copy.available;
    var priceHtml = priceMissing
      ? '<p class="bh-sf-card__price bh-sf-card__price--unavailable">' + escapeHtml(copy.priceUnavailable) + '</p>'
      : '<p class="bh-sf-card__price">' +
        escapeHtml(price) +
        (compare && compareCents != null && priceCents != null && compareCents > priceCents
          ? ' <s class="bh-sf-card__compare">' + escapeHtml(compare) + '</s>'
          : '') +
        '</p>';

    var addBtnHtml;
    if (demoMode) {
      addBtnHtml =
        '<button type="button" class="bh-sf-btn bh-sf-btn--primary bh-sf-btn--demo" disabled aria-disabled="true" title="' +
        escapeHtml(copy.demoHint) +
        '">' +
        escapeHtml(copy.demo) +
        '</button>';
    } else {
      addBtnHtml =
        '<button type="button" class="bh-sf-btn bh-sf-btn--primary" data-bh-sf-add data-bh-commerce-key="' +
        escapeHtml(key) +
        '"' +
        (ready ? '' : ' disabled') +
        '>' +
        escapeHtml(copy.add) +
        '</button>';
    }

    return (
      '<article class="bh-sf-card bh-sf-card--' +
      escapeHtml(mode) +
      ' bh-sf-skin--' +
      escapeHtml(skin || 'default') +
      '" data-bh-sf-card data-bh-commerce-key="' +
      escapeHtml(key) +
      '" data-product-id="' +
      escapeHtml(productId) +
      '" data-variant-id="' +
      escapeHtml(variantId) +
      '" data-shopify-product-id="' +
      escapeHtml(productId) +
      '" data-selected-variant-id="' +
      escapeHtml(variantId) +
      '" data-available="' +
      (soldOut ? 'false' : 'true') +
      '">' +
      '<div class="bh-sf-card__media">' +
      (img
        ? '<img src="' + escapeHtml(img) + '" alt="' + escapeHtml(alt) + '" loading="lazy" decoding="async" width="600" height="600">'
        : '<div class="bh-sf-card__media-empty" aria-hidden="true"></div>') +
      (soldOut ? '<span class="bh-sf-card__badge">' + escapeHtml(copy.soldOut) + '</span>' : '') +
      '</div>' +
      '<div class="bh-sf-card__body">' +
      (mode === 'favorite' ? '<p class="bh-sf-card__kicker">' + escapeHtml(copy.favorite) + '</p>' : '') +
      '<h3 class="bh-sf-card__title">' +
      escapeHtml(title) +
      '</h3>' +
      (variant ? '<p class="bh-sf-card__variant">' + escapeHtml(variant) + '</p>' : '') +
      priceHtml +
      '<p class="bh-sf-card__status">' +
      escapeHtml(status) +
      '</p>' +
      (mode === 'favorite' && note
        ? '<p class="bh-sf-card__note">' + escapeHtml(note.slice(0, 160)) + '</p>'
        : '') +
      '<div class="bh-sf-card__actions">' +
      '<button type="button" class="bh-sf-btn bh-sf-btn--secondary" data-bh-sf-more data-bh-commerce-key="' +
      escapeHtml(key) +
      '">' +
      escapeHtml(copy.more) +
      '</button>' +
      addBtnHtml +
      '</div>' +
      (demoMode ? '<p class="bh-sf-card__demo-hint">' + escapeHtml(copy.demoHint) + '</p>' : '') +
      '</div></article>'
    );
  }

  function setLoadState(root, state, count) {
    var meta = root.querySelector('[data-bh-sf-products-meta]');
    var loadingEl = root.querySelector('[data-bh-sf-count-loading]');
    var readyEl = root.querySelector('[data-bh-sf-count-ready]');
    var countEl = root.querySelector('[data-bh-sf-product-count]');
    var grid = root.querySelector('[data-bh-sf-product-grid]');
    var empty = root.querySelector('[data-bh-sf-products-empty]');
    if (meta) meta.setAttribute('data-bh-sf-load-state', state);
    if (state === 'loading' || state === 'unknown') {
      if (loadingEl) loadingEl.hidden = false;
      if (readyEl) readyEl.hidden = true;
      if (grid) {
        grid.classList.add('bh-sf-product-grid--loading');
        grid.setAttribute('aria-busy', 'true');
      }
      if (empty) empty.hidden = true;
      return;
    }
    if (loadingEl) loadingEl.hidden = true;
    if (readyEl) readyEl.hidden = false;
    if (countEl && typeof count === 'number') countEl.textContent = String(count);
    if (grid) {
      grid.classList.remove('bh-sf-product-grid--loading');
      grid.removeAttribute('aria-busy');
    }
  }

  function syncSectionChrome(root, state) {
    var copy = readCopy(root);
    var titleEl = root.querySelector('[data-bh-sf-section-title]');
    var leadEl = root.querySelector('[data-bh-sf-section-lead]');
    var expandBtn = root.querySelector('[data-bh-sf-expand]');
    var showShopTitle = state.expanded && state.needsExpand;
    if (titleEl) {
      titleEl.textContent = showShopTitle ? copy.titleShop : copy.titleFav;
    }
    if (leadEl && leadEl.hasAttribute('data-bh-sf-lead-fav')) {
      leadEl.hidden = showShopTitle;
    }
    if (leadEl && leadEl.hasAttribute('data-bh-sf-lead-shop')) {
      leadEl.hidden = !showShopTitle;
    }
    setLoadState(root, 'loaded', state.allProducts.length);
    if (expandBtn) {
      expandBtn.hidden = state.expanded || !state.needsExpand;
    }
    root.setAttribute('data-bh-sf-expanded', state.expanded ? 'true' : 'false');
    root.classList.toggle('bh-sf--expanded', state.expanded);
    root.classList.toggle('bh-sf--needs-expand', state.needsExpand && !state.expanded);
  }

  function renderGrid(root, state, options) {
    options = options || {};
    var skin = trim(options.skin || root.getAttribute('data-bh-sf-skin') || 'default');
    var locale = trim(options.locale || root.getAttribute('data-bh-sf-locale') || 'nl');
    var copy = readCopy(root);
    var demoMode = !isAttributableRoot(root) && !isSampleCartRoot(root);
    var grid = root.querySelector('[data-bh-sf-product-grid]');
    var empty = root.querySelector('[data-bh-sf-products-empty]');
    if (!grid) return;

    syncSectionChrome(root, state);

    if (!state.visibleProducts.length) {
      grid.innerHTML = '';
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;
    grid.innerHTML = state.visibleProducts
      .map(function (p, i) {
        return cardHtml(p, cardModeForProduct(p, i, state), skin, locale, { copy: copy, demoMode: demoMode });
      })
      .join('');
  }

  function renderMount(root, products, options) {
    options = options || {};
    var expanded = root._bhSfState && root._bhSfState.expanded;
    var state = buildProductState(products, expanded);
    root._bhSfMap = buildCommerceMap(state.allProducts);
    root._bhSfState = state;
    renderGrid(root, state, options);
    updateCartShell(root, { count: 0, total_cents: 0, preview: true });
  }

  function expandProducts(root) {
    if (!root._bhSfState) return;
    root._bhSfState = buildProductState(root._bhSfState.allProducts, true);
    renderGrid(root, root._bhSfState, { skin: root.getAttribute('data-bh-sf-skin') });
  }

  function updateCartShell(root, state) {
    state = state || {};
    var locale = trim(root.getAttribute('data-bh-sf-locale') || 'nl');
    var count = Number(state.count) || 0;
    var total = money(state.total_cents || 0, locale);
    root.querySelectorAll('[data-bh-sf-cart-count]').forEach(function (el) {
      el.textContent = String(count);
    });
    root.querySelectorAll('[data-bh-sf-cart-total]').forEach(function (el) {
      el.textContent = total || '€0.00';
    });
    var sticky = root.querySelector('[data-bh-sf-sticky-cart]');
    if (sticky) sticky.hidden = count < 1;
  }

  function findDetailTemplate(root, key) {
    var panels = root.querySelector('[data-bh-sf-detail-panels]') || document.querySelector('[data-bh-sf-detail-panels]');
    if (!panels) return null;
    return panels.querySelector('[data-bh-sf-detail-template="' + key.replace(/"/g, '') + '"]');
  }

  function usesApiDetailPath(root) {
    return shouldFetchLiveSelection(root);
  }

  function detailCache(root) {
    if (!root._bhSfDetailCache) root._bhSfDetailCache = Object.create(null);
    return root._bhSfDetailCache;
  }

  function drawerLoadingHtml(root) {
    var locale = root.getAttribute('data-bh-sf-locale') || 'nl';
    var en = String(locale).toLowerCase().indexOf('en') === 0;
    return (
      '<p class="bh-sf-drawer__loading" data-bh-detail-loading>' +
      escapeHtml(en ? 'Loading product information…' : 'Productinformatie laden…') +
      '</p>'
    );
  }

  function drawerErrorHtml(root) {
    var locale = root.getAttribute('data-bh-sf-locale') || 'nl';
    var en = String(locale).toLowerCase().indexOf('en') === 0;
    return (
      '<p class="bh-sf-drawer__detail-fallback" data-bh-detail-error>' +
      escapeHtml(
        en
          ? 'Detailed product information is temporarily unavailable. You can still add this product to your cart below.'
          : 'Gedetailleerde productinformatie is tijdelijk niet beschikbaar. Je kunt dit product hieronder nog wel toevoegen aan je winkelwagen.'
      ) +
      '</p>'
    );
  }

  function fetchProductDetailFromApi(root, product) {
    var ref = trim(root.getAttribute('data-bh-sf-partner-ref'));
    var base = trim(root.getAttribute('data-bh-sf-api-base')).replace(/\/$/, '');
    var productId = trim(product.shopify_product_id || product.id);
    var variantId = trim(product.selected_variant_id || product.variant_id);
    var locale = trim(root.getAttribute('data-bh-sf-locale') || 'nl');
    var url =
      base +
      '/api/public/partner-storefront-commerce/' +
      encodeURIComponent(ref) +
      '/products/' +
      encodeURIComponent(productId) +
      '/detail?variant_id=' +
      encodeURIComponent(variantId) +
      '&locale=' +
      encodeURIComponent(locale);
    return fetch(url, {
      method: 'GET',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) {
        return res
          .json()
          .catch(function () {
            return {};
          })
          .then(function (data) {
            return { ok: res.ok, data: data };
          });
      })
      .then(function (result) {
        if (!result.ok || !result.data || !result.data.ok || !result.data.detail) return null;
        return result.data.detail;
      })
      .catch(function () {
        return null;
      });
  }

  function detailHasProductgids(detail) {
    if (!detail) return false;
    if (detail.detail_source === 'productgids' || detail.detail_source === 'productgids_section') return true;
    if (trim(detail.intro) || trim(detail.directions) || trim(detail.warnings) || trim(detail.storage)) return true;
    if (Array.isArray(detail.faq_items) && detail.faq_items.length) return true;
    if (Array.isArray(detail.ingredient_rows) && detail.ingredient_rows.length) return true;
    if (Array.isArray(detail.composition_rows) && detail.composition_rows.length) return true;
    if (Array.isArray(detail.quick_facts) && detail.quick_facts.length) return true;
    return false;
  }

  function fetchProductgidsFragmentHtml(product, root) {
    var handle = trim(product.product_handle || product.handle);
    var variantId = trim(product.selected_variant_id || product.variant_id);
    if (!handle || !variantId) return Promise.resolve(null);
    var url =
      '/products/' +
      encodeURIComponent(handle) +
      '?sections=bee-hive-partner-product-detail-fragment&variant=' +
      encodeURIComponent(variantId);
    if (root && typeof window !== 'undefined' && window.location && window.location.search) {
      var preview = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (preview) url += '&preview_theme_id=' + encodeURIComponent(preview);
    }
    return fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
    })
      .then(function (res) {
        return res
          .text()
          .then(function (text) {
            return { ok: res.ok, text: text };
          });
      })
      .then(function (result) {
        if (!result.ok || !result.text) return null;
        var text = trim(result.text);
        if (!text) return null;
        try {
          var data = JSON.parse(text);
          var html = data && data['bee-hive-partner-product-detail-fragment'];
          html = trim(html);
          return html || null;
        } catch (e) {
          return null;
        }
      })
      .catch(function () {
        return null;
      });
  }

  function resolveProductDetail(root, product, key) {
    if (!usesApiDetailPath(root)) {
      if (product.detail || product.product_detail) {
        return Promise.resolve(product.detail || product.product_detail);
      }
      return Promise.resolve(null);
    }
    var cache = detailCache(root);
    if (cache[key]) return Promise.resolve(cache[key]);
    return fetchProductDetailFromApi(root, product)
      .then(function (detail) {
        detail = detail || {};
        if (detailHasProductgids(detail)) {
          cache[key] = detail;
          return detail;
        }
        return fetchProductgidsFragmentHtml(product, root).then(function (fragmentHtml) {
          var merged = Object.assign({}, detail);
          if (fragmentHtml) {
            merged._fragment_html = fragmentHtml;
            merged.detail_source = 'productgids_section';
          }
          if (detailHasProductgids(merged) || fragmentHtml || trim(merged.description_excerpt)) {
            cache[key] = merged;
          }
          return merged;
        });
      })
      .catch(function () {
        return fetchProductgidsFragmentHtml(product, root).then(function (fragmentHtml) {
          if (!fragmentHtml) return null;
          var merged = { _fragment_html: fragmentHtml, detail_source: 'productgids_section' };
          cache[key] = merged;
          return merged;
        });
      });
  }

  function fillDrawerBody(root, product, key, opts) {
    opts = opts || {};
    var body = root.querySelector('[data-bh-sf-drawer-body]');
    if (!body) return;
    if (opts.loading) {
      body.innerHTML = drawerLoadingHtml(root);
      return;
    }
    body.innerHTML = '';
    var detail = opts.detail || product.detail || product.product_detail || null;
    if (opts.error && !detail) {
      body.innerHTML = drawerErrorHtml(root);
      return;
    }
    if (!usesApiDetailPath(root)) {
      var tpl = findDetailTemplate(root, key);
      if (tpl && tpl.content) {
        body.appendChild(tpl.content.cloneNode(true));
        return;
      }
    }
    if (detail && trim(detail._fragment_html)) {
      body.innerHTML = detail._fragment_html;
      return;
    }
    if (detail && global.BeeHivePartnerProductDetail && typeof global.BeeHivePartnerProductDetail.renderDetailHtml === 'function') {
      body.innerHTML = global.BeeHivePartnerProductDetail.renderDetailHtml(detail, {
        locale: detail.locale || root.getAttribute('data-bh-sf-locale') || 'nl',
      });
      return;
    }
    if (trim(product.partner_note)) {
      var locale = root.getAttribute('data-bh-sf-locale') || 'nl';
      var partnerTitle = String(locale).indexOf('en') === 0 ? 'Why I chose this' : 'Mijn keuze';
      body.innerHTML =
        '<section class="bh-sf-detail__partner"><h3 class="bh-sf-detail__partner-title">' +
        escapeHtml(partnerTitle) +
        '</h3><p class="bh-sf-detail__partner-body">' +
        escapeHtml(product.partner_note) +
        '</p></section>';
    }
  }

  function syncQtyControls(dialog, product) {
    var input = dialog.querySelector('[data-bh-sf-qty]');
    var display = dialog.querySelector('[data-bh-sf-qty-display]');
    if (!input) return;
    var min = Number(product.quantity_min);
    var step = Number(product.quantity_increment);
    if (!isFinite(min) || min < 1) min = 1;
    if (!isFinite(step) || step < 1) step = 1;
    input.min = String(min);
    input.step = String(step);
    input.value = String(min);
    if (display) display.textContent = String(min);
    dialog._bhSfQtyMin = min;
    dialog._bhSfQtyStep = step;
  }

  function bumpDrawerQty(root, delta) {
    var dialog = root.querySelector('[data-bh-sf-drawer]');
    if (!dialog) return;
    var input = dialog.querySelector('[data-bh-sf-qty]');
    var display = dialog.querySelector('[data-bh-sf-qty-display]');
    if (!input) return;
    var min = dialog._bhSfQtyMin || Number(input.min) || 1;
    var step = dialog._bhSfQtyStep || Number(input.step) || 1;
    var current = Number(input.value);
    if (!isFinite(current)) current = min;
    var next = current + delta * step;
    if (next < min) next = min;
    input.value = String(next);
    if (display) display.textContent = String(next);
  }

  function syncDrawerAddButton(root, product, key) {
    var dialog = root.querySelector('[data-bh-sf-drawer]');
    if (!dialog) return;
    var addBtn = dialog.querySelector('[data-bh-sf-drawer-add]');
    if (!addBtn) return;
    var copy = readCopy(root);
    var demoMode = !isAttributableRoot(root) && !isSampleCartRoot(root);
    var soldOut = product.sold_out || product.available === false;
    var priceMissing = parseCents(product.price_cents) == null;
    var missingVariant =
      product.variant_exists === false ||
      product.commerce_block_reason === 'missing_selected_variant' ||
      product.needs_variant_selection;
    var ready = product.commerce_ready !== false && !soldOut && !priceMissing && !missingVariant;
    if (demoMode) {
      addBtn.disabled = true;
      addBtn.setAttribute('aria-disabled', 'true');
      addBtn.classList.add('bh-sf-btn--demo');
      addBtn.removeAttribute('data-bh-sf-add');
      addBtn.removeAttribute('data-bh-commerce-key');
      addBtn.textContent = copy.demo;
      addBtn.title = copy.demoHint;
      return;
    }
    addBtn.classList.remove('bh-sf-btn--demo');
    addBtn.textContent = copy.add;
    addBtn.removeAttribute('title');
    addBtn.disabled = !ready;
    if (ready) addBtn.removeAttribute('data-bh-keep-disabled');
    else addBtn.setAttribute('data-bh-keep-disabled', '1');
    addBtn.setAttribute('data-bh-sf-add', '');
    if (key) addBtn.setAttribute('data-bh-commerce-key', key);
  }

  function openDrawer(root, key, triggerEl) {
    var map = root._bhSfMap || {};
    var product = map[key];
    var dialog = root.querySelector('[data-bh-sf-drawer]');
    if (!dialog || !product) return;
    var locale = trim(root.getAttribute('data-bh-sf-locale') || 'nl');
    root._bhSfLastFocus = triggerEl || document.activeElement;
    var title = trim(product.title) || 'Product';
    var set = function (sel, text) {
      var el = dialog.querySelector(sel);
      if (el) el.textContent = text || '';
    };
    set('[data-bh-sf-drawer-vendor]', product.vendor || '');
    set('[data-bh-sf-drawer-title]', title);
    set('[data-bh-sf-drawer-variant]', product.selected_variant_label || product.variant_label || '');
    var compare = money(product.compare_at_price_cents, locale);
    var priceText =
      parseCents(product.price_cents) == null
        ? 'Price unavailable'
        : money(product.price_cents, locale) +
          (compare &&
          parseCents(product.compare_at_price_cents) != null &&
          parseCents(product.compare_at_price_cents) > parseCents(product.price_cents)
            ? ' · was ' + compare
            : '');
    set('[data-bh-sf-drawer-price]', priceText);
    var soldOut = product.sold_out || product.available === false;
    var priceMissing = parseCents(product.price_cents) == null;
    var missingVariant =
      product.variant_exists === false ||
      product.commerce_block_reason === 'missing_selected_variant' ||
      product.needs_variant_selection;
    var status = soldOut
      ? 'Sold out'
      : missingVariant
        ? 'Option unavailable'
        : priceMissing
          ? 'Price unavailable'
          : product.commerce_ready === false
            ? trim(product.commerce_block_reason || 'Unavailable')
            : 'Available';
    set('[data-bh-sf-drawer-status]', status);

    var img = dialog.querySelector('[data-bh-sf-drawer-img]');
    if (img) {
      var src = product.display_image_url || product.variant_image_url || product.image_url || '';
      if (src) {
        img.src = src;
        img.alt = title;
        img.hidden = false;
      } else {
        img.removeAttribute('src');
        img.hidden = true;
      }
    }

    fillDrawerBody(root, product, key, {
      loading: usesApiDetailPath(root) && !detailCache(root)[key] && !product.detail && !product.product_detail,
    });
    syncQtyControls(dialog, product);
    root._bhSfDrawerProduct = product;
    root._bhSfDrawerKey = key;
    syncDrawerAddButton(root, product, key);

    dialog.hidden = false;
    dialog.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('bh-sf-drawer-open');
    var closeBtn = dialog.querySelector('[data-bh-sf-drawer-close]');
    var panel = dialog.querySelector('[data-bh-sf-drawer-panel]');
    if (closeBtn) closeBtn.focus();
    else if (panel) panel.focus();

    var requestId = (root._bhSfDetailRequestId = (root._bhSfDetailRequestId || 0) + 1);
    resolveProductDetail(root, product, key).then(function (detail) {
      if (root._bhSfDrawerKey !== key || requestId !== root._bhSfDetailRequestId) return;
      if (detail) {
        product.detail = detail;
        if (detail.partner_note) product.partner_note = detail.partner_note;
        map[key] = product;
        root._bhSfMap = map;
        root._bhSfDrawerProduct = product;
      }
      if (!detail && !product.detail && !product.product_detail && usesApiDetailPath(root)) {
        fillDrawerBody(root, product, key, { error: true });
      } else {
        var resolvedDetail = detail || product.detail || product.product_detail;
        if (
          resolvedDetail &&
          !detailHasProductgids(resolvedDetail) &&
          !trim(resolvedDetail._fragment_html) &&
          !trim(resolvedDetail.description_excerpt) &&
          usesApiDetailPath(root)
        ) {
          fillDrawerBody(root, product, key, { error: true });
        } else {
          fillDrawerBody(root, product, key, { detail: resolvedDetail });
        }
      }
      syncDrawerAddButton(root, product, key);
    });
  }

  function closeDrawer(root) {
    var dialog = root.querySelector('[data-bh-sf-drawer]');
    if (!dialog) return;
    dialog.hidden = true;
    dialog.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('bh-sf-drawer-open');
    var body = dialog.querySelector('[data-bh-sf-drawer-body]');
    if (body) body.innerHTML = '';
    var restore = root._bhSfLastFocus;
    root._bhSfLastFocus = null;
    if (restore && typeof restore.focus === 'function') {
      try {
        restore.focus();
      } catch (e) {}
    }
  }

  function openCart(root) {
    var panel = root.querySelector('[data-bh-sf-minicart]');
    if (!panel) return;
    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');
  }

  function closeCart(root) {
    var panel = root.querySelector('[data-bh-sf-minicart]');
    if (!panel) return;
    panel.hidden = true;
    panel.setAttribute('aria-hidden', 'true');
  }

  function bind(root) {
    if (!root || root._bhSfBound) return;
    root._bhSfBound = true;

    root.addEventListener('click', function (event) {
      var expand = event.target.closest('[data-bh-sf-expand]');
      if (expand) {
        event.preventDefault();
        expandProducts(root);
        return;
      }
      var more = event.target.closest('[data-bh-sf-more]');
      if (more) {
        event.preventDefault();
        openDrawer(root, more.getAttribute('data-bh-commerce-key'), more);
        return;
      }
      var add = event.target.closest('[data-bh-sf-add]');
      if (add) {
        event.preventDefault();
        var addKey = add.getAttribute('data-bh-commerce-key');
        var product = (root._bhSfMap && addKey && root._bhSfMap[addKey]) || root._bhSfDrawerProduct;
        if (global.BeeHivePartnerCart && typeof global.BeeHivePartnerCart.addToCart === 'function') {
          global.BeeHivePartnerCart.addToCart(root, product, { openCart: true });
        } else {
          openCart(root);
        }
        return;
      }
      if (event.target.closest('[data-bh-sf-drawer-close],[data-bh-sf-drawer-backdrop]')) {
        closeDrawer(root);
        return;
      }
      if (event.target.closest('[data-bh-sf-cart-open]')) {
        event.preventDefault();
        openCart(root);
        return;
      }
      if (event.target.closest('[data-bh-sf-minicart-close],[data-bh-sf-minicart-backdrop]')) {
        closeCart(root);
        return;
      }
      if (event.target.closest('[data-bh-sf-checkout]')) {
        event.preventDefault();
        if (global.BeeHivePartnerCart && typeof global.BeeHivePartnerCart.checkout === 'function') {
          global.BeeHivePartnerCart.checkout(root);
        }
        return;
      }
      var removeBtn = event.target.closest('[data-bh-sf-remove]');
      if (removeBtn && global.BeeHivePartnerCart) {
        event.preventDefault();
        global.BeeHivePartnerCart.removeLine(root, removeBtn.getAttribute('data-line-key'));
        return;
      }
      var inc = event.target.closest('[data-bh-sf-qty-inc]');
      if (inc && global.BeeHivePartnerCart) {
        event.preventDefault();
        global.BeeHivePartnerCart.bumpQty(root, inc.getAttribute('data-line-key'), 1);
        return;
      }
      var dec = event.target.closest('[data-bh-sf-qty-dec]');
      if (dec && global.BeeHivePartnerCart) {
        event.preventDefault();
        global.BeeHivePartnerCart.bumpQty(root, dec.getAttribute('data-line-key'), -1);
        return;
      }
      if (event.target.closest('[data-bh-sf-qty-dec-drawer]')) {
        event.preventDefault();
        bumpDrawerQty(root, -1);
        return;
      }
      if (event.target.closest('[data-bh-sf-qty-inc-drawer]')) {
        event.preventDefault();
        bumpDrawerQty(root, 1);
        return;
      }
      if (event.target.closest('[data-bh-sf-drawer-continue]')) {
        event.preventDefault();
        closeDrawer(root);
        return;
      }
      if (event.target.closest('[data-bh-sf-minicart-continue]')) {
        event.preventDefault();
        closeCart(root);
        return;
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closeDrawer(root);
        closeCart(root);
      }
    });
  }

  function shouldFetchLiveSelection(root) {
    if (String(root.getAttribute('data-bh-sf-prefer-api') || '') !== 'true') return false;
    if (!isAttributableRoot(root)) return false;
    return Boolean(trim(root.getAttribute('data-bh-sf-partner-ref')) && trim(root.getAttribute('data-bh-sf-api-base')));
  }

  function fetchLivePartnerProducts(root) {
    var ref = trim(root.getAttribute('data-bh-sf-partner-ref'));
    var base = trim(root.getAttribute('data-bh-sf-api-base')).replace(/\/$/, '');
    var url = base + '/api/public/partner-storefront-commerce/' + encodeURIComponent(ref);
    return fetch(url, {
      method: 'GET',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) {
        return res.json().catch(function () {
          return {};
        }).then(function (data) {
          return { ok: res.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok || !result.data || result.data.ok === false) return null;
        return Array.isArray(result.data.products) ? result.data.products : [];
      })
      .catch(function () {
        return null;
      });
  }

  function parseEmbeddedProducts(root) {
    var node = root.querySelector('[data-bh-sf-products-json]');
    if (!node) return [];
    try {
      var data = JSON.parse(node.textContent || '[]');
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return [];
    }
  }

  async function hydrateIfNeeded(products) {
    if (!products.length) return products;
    var needs = products.some(function (p) {
      if (p.source === 'partner_selection') {
        return p.price_cents == null || p.commerce_ready !== true;
      }
      return p.price_cents == null || p.commerce_ready == null;
    });
    if (!needs || !global.BeeHivePartnerCommerce || typeof global.BeeHivePartnerCommerce.hydrateSelections !== 'function') {
      return products;
    }
    try {
      return await global.BeeHivePartnerCommerce.hydrateSelections(products);
    } catch (e) {
      return products;
    }
  }

  function initRoot(root) {
    bind(root);
    setLoadState(root, 'loading');
    var embedded = parseEmbeddedProducts(root);
    var loadPromise;

    if (shouldFetchLiveSelection(root)) {
      loadPromise = fetchLivePartnerProducts(root).then(function (apiProducts) {
        if (apiProducts !== null) return apiProducts;
        return embedded;
      });
    } else {
      loadPromise = Promise.resolve(embedded);
    }

    loadPromise
      .then(function (products) {
        return hydrateIfNeeded(products);
      })
      .then(function (live) {
        renderMount(root, live, { skin: root.getAttribute('data-bh-sf-skin') });
        setLoadState(root, 'loaded', Array.isArray(live) ? live.length : 0);
        if (global.BeeHivePartnerCart && typeof global.BeeHivePartnerCart.hydrate === 'function') {
          global.BeeHivePartnerCart.hydrate(root).catch(function () {});
        }
      })
      .catch(function () {
        setLoadState(root, 'error', 0);
        var empty = root.querySelector('[data-bh-sf-products-empty]');
        if (empty) {
          empty.hidden = false;
          empty.textContent =
            (root.getAttribute('data-bh-sf-locale') || '').indexOf('en') === 0
              ? 'Products could not be loaded.'
              : 'Producten konden niet worden geladen.';
        }
        var grid = root.querySelector('[data-bh-sf-product-grid]');
        if (grid) {
          grid.innerHTML = '';
          grid.classList.remove('bh-sf-product-grid--loading');
          grid.removeAttribute('aria-busy');
        }
      });
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-bh-partner-storefront]');
    roots.forEach(initRoot);
  }

  global.BeeHivePartnerStorefront = {
    CONTRACT: CONTRACT,
    FAVORITES_MAX: FAVORITES_MAX,
    SHOP_MAX: SHOP_MAX,
    parseCents: parseCents,
    money: money,
    splitFavoritesShop: splitFavoritesShop,
    buildProductState: buildProductState,
    buildCommerceMap: buildCommerceMap,
    renderMount: renderMount,
    renderGrid: renderGrid,
    expandProducts: expandProducts,
    initAll: initAll,
    initRoot: initRoot,
    cardHtml: cardHtml,
    openCart: openCart,
    closeCart: closeCart,
    openDrawer: openDrawer,
    closeDrawer: closeDrawer,
    isAttributableRoot: isAttributableRoot,
    isSampleCartRoot: isSampleCartRoot,
    usesDemoCommerceUi: usesDemoCommerceUi,
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('click', function (event) {
      var openBtn = event.target.closest('[data-bh-sf-cart-open]');
      if (!openBtn) return;
      var root =
        openBtn.closest('[data-bh-partner-storefront]') ||
        document.querySelector('[data-bh-partner-storefront]');
      if (!root) return;
      event.preventDefault();
      openCart(root);
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        initAll(document);
      });
    } else {
      initAll(document);
    }
  }
})(typeof window !== 'undefined' ? window : globalThis);
